import { useState, useEffect, useCallback } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Input, Modal, ModalClose, ModalDialog, Sheet, Stack, Tooltip, Typography } from '@mui/joy'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ScienceIcon from '@mui/icons-material/Science'
import type { SourceBatch } from 'api/oracleApi'

export { ScienceIcon }

function ArtifactRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
    return (
        <Stack direction="row" gap={1.5} sx={{ mb: 1 }}>
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: 92, pt: 0.5, fontFamily: 'ui-monospace, Menlo, monospace' }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Stack>
    )
}

function SourceBatchArtifact({ batch, index, total }: { batch: SourceBatch; index?: number; total?: number }): JSX.Element {
    const [copied, setCopied] = useState(false)
    const handleCopy = useCallback(() => {
        navigator.clipboard?.writeText(JSON.stringify(batch, null, 2)).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        })
    }, [batch])

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 1.25 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                    {index && total ? `Batch ${index} / ${total}` : 'Single batch'}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip size="sm" variant="soft" color="neutral" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {batch.sourceType}
                </Chip>
                <Tooltip title={copied ? 'Copied' : 'Copy JSON'} size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" onClick={handleCopy}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <ArtifactRow label="sourceIds">
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                    {batch.sourceIds.map((id) => (
                        <Chip key={id} size="sm" variant="outlined" color="neutral" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 }}>
                            {id}
                        </Chip>
                    ))}
                </Stack>
            </ArtifactRow>

            <ArtifactRow label="text">
                <Sheet variant="soft" color="neutral" sx={{ borderRadius: 'sm', p: 1, maxHeight: 280, overflow: 'auto' }}>
                    {batch.text ? (
                        <Typography level="body-xs" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', whiteSpace: 'pre-wrap' }}>
                            {batch.text}
                        </Typography>
                    ) : (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            (empty)
                        </Typography>
                    )}
                </Sheet>
            </ArtifactRow>

            {batch.hints?.contacts && batch.hints.contacts.length > 0 && (
                <ArtifactRow label="contacts">
                    <Stack gap={0.25}>
                        {batch.hints.contacts.map((c, i) => (
                            <Typography key={i} level="body-xs" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                {c.email ? `email: ${c.email}` : ''}
                                {c.phone ? ` · phone: ${c.phone}` : ''}
                                {c.name ? ` · name: "${c.name}"` : ''}
                            </Typography>
                        ))}
                    </Stack>
                </ArtifactRow>
            )}

            {batch.hints?.attributions && batch.hints.attributions.length > 0 && (
                <ArtifactRow label="attributions">
                    <Stack gap={0.25}>
                        {batch.hints.attributions.map((a, i) => (
                            <Typography key={i} level="body-xs" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                                {a.system} · externalId: {a.externalId}
                                {a.nameHint ? ` · name: "${a.nameHint}"` : ''}
                            </Typography>
                        ))}
                    </Stack>
                </ArtifactRow>
            )}
        </Sheet>
    )
}

export function EtlPreviewModal({
    open,
    onClose,
    onRunPreview,
}: {
    open: boolean
    onClose: () => void
    onRunPreview: (limit: number) => Promise<SourceBatch[]>
}): JSX.Element {
    const [batches, setBatches] = useState<SourceBatch[] | null>(null)
    const [limit, setLimit] = useState(25)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const run = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const b = await onRunPreview(limit)
            setBatches(b)
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [limit, onRunPreview])

    useEffect(() => {
        if (!open) return
        setBatches(null)
        void run()
        // run intentionally excluded — we only want to trigger on open/close
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ minWidth: 720, maxWidth: 920, maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="title-md">Dry Run ETL</Typography>
                <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                    Shows the SourceBatch[] the next pipeline run would emit. Cursor and status are not modified.
                </Typography>
                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography level="body-xs" sx={{ minWidth: 64, color: 'text.tertiary' }}>
                        Max rows
                    </Typography>
                    <Input
                        size="sm"
                        type="number"
                        value={limit}
                        onChange={(e) => setLimit(Math.max(1, Math.min(100, Number(e.target.value) || 0)))}
                        sx={{ width: 90 }}
                    />
                    <Button size="sm" variant="solid" color="primary" loading={loading} onClick={() => void run()}>
                        Run
                    </Button>
                </Stack>
                {error && (
                    <Alert color="danger" size="sm" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}
                {loading && !batches && (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress size="sm" />
                    </Stack>
                )}
                {!loading && batches && batches.length === 0 && (
                    <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                        <Typography level="body-sm" color="neutral">
                            Nothing pending. The next pipeline run would be a no-op.
                        </Typography>
                    </Sheet>
                )}
                {batches && batches.length > 0 && (
                    <Stack gap={1.5} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                        {batches.map((b, i) => (
                            <SourceBatchArtifact key={i} batch={b} index={i + 1} total={batches.length} />
                        ))}
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    )
}
